import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["tag", "color", "className", "style", "pill", "children"];
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

/**
 * Badge component that replaces reactstrap Badge to avoid defaultProps warnings.
 *
 * This custom wrapper exists because reactstrap's Badge component uses defaultProps,
 * which triggers deprecation warnings in React 18+. This component maintains full
 * API compatibility with reactstrap's Badge by:
 * - Using default parameters instead of defaultProps
 * - Spreading remaining props ({...rest}) to support all reactstrap Badge props
 * - Replicating the same className logic and structure
 *
 * @see https://react.dev/reference/react/Component#static-defaultprops
 *      React 18+ deprecates defaultProps for function components
 *
 * @param {Object} props - Component props
 * @param {string|Function} [props.tag='span'] - HTML tag or component to render
 * @param {string} [props.color='secondary'] - Badge color variant
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {Object} [props.style={}] - Inline styles
 * @param {boolean} [props.pill=false] - Whether to render as a pill-shaped badge
 * @param {React.ReactNode} [props.children=null] - Badge content
 * @param {*} props.rest - Any additional props are spread to the rendered element
 *
 * @example
 * // Drop-in replacement for reactstrap Badge
 * <Badge color="primary" pill>New</Badge>
 * <Badge tag="div" style={{ marginTop: 10 }}>Custom</Badge>
 *
 * @note This wrapper is warranted because:
 * - It's used across 8+ components in the qualityAgent module
 * - It eliminates console warnings without changing component APIs
 * - Prop spreading maintains compatibility with reactstrap's full API
 * - If reactstrap updates their Badge to remove defaultProps, this wrapper can be removed
 */
const Badge = _ref => {
  let _ref$tag = _ref.tag,
    Tag = _ref$tag === void 0 ? 'span' : _ref$tag,
    _ref$color = _ref.color,
    color = _ref$color === void 0 ? 'secondary' : _ref$color,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$style = _ref.style,
    style = _ref$style === void 0 ? {} : _ref$style,
    _ref$pill = _ref.pill,
    pill = _ref$pill === void 0 ? false : _ref$pill,
    _ref$children = _ref.children,
    children = _ref$children === void 0 ? null : _ref$children,
    rest = _objectWithoutProperties(_ref, _excluded);
  const badgeClasses = classNames('badge', "badge-".concat(color), {
    'badge-pill': pill
  }, className);
  return (
    /*#__PURE__*/
    // Prop spreading is intentional to maintain API compatibility with reactstrap Badge
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement(Tag, _extends({
      className: badgeClasses,
      style: style
    }, rest), children)
  );
};
Badge.propTypes = {
  // Defaults are handled via default parameters in the function signature, not defaultProps
  // This avoids React 18+ deprecation warnings while maintaining API compatibility
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  // default: 'span'
  color: PropTypes.string,
  // default: 'secondary'
  className: PropTypes.string,
  // default: ''
  style: PropTypes.shape({}),
  // default: {}
  pill: PropTypes.bool,
  // default: false
  children: PropTypes.node // default: null
};
export default Badge;